import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../utils/cn.js';
export function FormField({ label, hint, error, success, required, children, className, ...rest }) {
    return (_jsxs("div", { className: cn('sak-field', error ? 'sak-field--error' : undefined, success && !error ? 'sak-field--success' : undefined, className), ...rest, children: [_jsxs("label", { className: "sak-field__label", children: [label, required ? _jsx("span", { "aria-hidden": "true", children: " *" }) : null] }), children, hint && !error ? _jsx("span", { className: "sak-field__hint", children: hint }) : null, error ? (_jsx("span", { className: "sak-field__error", role: "alert", children: error })) : null] }));
}
export function FormSection({ title, children, className, ...rest }) {
    return (_jsxs("section", { className: cn('sak-form-section', className), ...rest, children: [_jsx("h3", { className: "sak-form-section__title", children: title }), children] }));
}
export function Alert({ tone = 'info', title, children, className, ...rest }) {
    return (_jsx("div", { className: cn('sak-alert', `sak-alert--${tone}`, className), role: "alert", ...rest, children: _jsxs("div", { children: [title ? _jsx("strong", { children: title }) : null, _jsx("div", { children: children })] }) }));
}
export function ToastStack({ toasts }) {
    if (toasts.length === 0)
        return null;
    return (_jsx("div", { className: "sak-toast-stack", "aria-live": "polite", children: toasts.map((t) => (_jsx(Toast, { message: t.message }, t.id))) }));
}
export function Toast({ message, tone = 'info' }) {
    return (_jsx("div", { className: cn('sak-toast', tone !== 'info' && `sak-toast--${tone}`), role: "status", children: message }));
}
export function EmptyState({ title, description, action }) {
    return (_jsxs("div", { className: "sak-empty-state", children: [_jsx("h3", { className: "sak-empty-state__title", children: title }), description ? _jsx("p", { className: "sak-empty-state__desc", children: description }) : null, action] }));
}
/**
 * Maps HTTP status codes to user-facing Persian title and description.
 * Never leaks raw English server error messages into the UI.
 */
export function resolveHttpError(status, fallbackMessage) {
    if (status === 403) {
        return {
            title: 'دسترسی ندارید',
            description: 'اجازهٔ دسترسی به این بخش را ندارید.',
            canRetry: false,
        };
    }
    if (status === 404) {
        return {
            title: 'یافت نشد',
            description: 'اطلاعات مورد نظر یافت نشد یا ممکن است حذف شده باشد.',
            canRetry: true,
        };
    }
    if (status === 422) {
        return {
            title: 'اطلاعات نامعتبر است',
            description: 'اطلاعات ارسال‌شده معتبر نیست. لطفاً ورودی‌ها را بررسی کرده و دوباره تلاش کنید.',
            canRetry: true,
        };
    }
    if (status === 500) {
        return {
            title: 'خطای سرور',
            description: 'خطایی در سرور رخ داده است. لطفاً دوباره تلاش کنید.',
            canRetry: true,
        };
    }
    if (status === 503) {
        return {
            title: 'در حال به‌روزرسانی',
            description: 'سامانه در حال به‌روزرسانی است. لطفاً چند لحظه بعد دوباره تلاش کنید.',
            canRetry: true,
        };
    }
    // Never leak English server error messages directly to the UI
    const isEnglishOnly = typeof fallbackMessage === 'string' &&
        fallbackMessage.trim().length > 0 &&
        /^[A-Za-z0-9\s.,!?:;'"_\-–—/\\()[\]{}]+$/.test(fallbackMessage.trim());
    const safeDesc = fallbackMessage && !isEnglishOnly
        ? fallbackMessage
        : 'امکان برقراری ارتباط با سرور وجود ندارد. لطفاً دوباره تلاش کنید.';
    return {
        title: 'خطای ارتباط با سرور',
        description: safeDesc,
        canRetry: true,
    };
}
export function ErrorState({ title, description, retry, status, onRetry, retryLabel = 'تلاش دوباره', }) {
    const resolved = status !== undefined && status !== null ? resolveHttpError(status) : null;
    const effectiveTitle = title ?? resolved?.title ?? 'خطا در بارگذاری';
    const effectiveDesc = description ?? resolved?.description;
    const retryButton = retry ??
        (onRetry ? (_jsx("button", { type: "button", className: "sak-btn sak-btn--secondary", onClick: onRetry, children: retryLabel })) : null);
    return (_jsxs("div", { className: "sak-error-state", role: "alert", children: [_jsx("h3", { className: "sak-error-state__title", children: effectiveTitle }), effectiveDesc ? _jsx("p", { className: "sak-error-state__desc", children: effectiveDesc }) : null, retryButton] }));
}
export function LoadingState({ label = 'در حال بارگذاری…' }) {
    return (_jsxs("div", { className: "sak-loading-state", role: "status", "aria-live": "polite", children: [_jsx("div", { className: "sak-spinner", "aria-hidden": "true" }), _jsx("span", { children: label })] }));
}
export function Skeleton({ variant = 'text', className, ...rest }) {
    return (_jsx("div", { className: cn('sak-skeleton', `sak-skeleton--${variant}`, className), "aria-hidden": "true", ...rest }));
}
export function TableSkeleton({ rows = 5, columns = 4, className, ...rest }) {
    return (_jsxs("div", { className: cn('sak-table-skeleton', className), role: "status", "aria-live": "polite", "aria-label": "\u062F\u0631 \u062D\u0627\u0644 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC", ...rest, children: [_jsx("div", { className: "sak-table-skeleton__head", children: Array.from({ length: columns }, (_, i) => (_jsx(Skeleton, { variant: "text", className: "sak-table-skeleton__cell" }, `h-${i}`))) }), Array.from({ length: rows }, (_, row) => (_jsx("div", { className: "sak-table-skeleton__row", children: Array.from({ length: columns }, (_, col) => (_jsx(Skeleton, { variant: "text", className: "sak-table-skeleton__cell" }, col))) }, row)))] }));
}
export function UnavailableState({ title = 'در نسخه فعلی در دسترس نیست', description, action, }) {
    return (_jsxs("div", { className: "sak-unavailable-state", role: "status", children: [_jsx("h3", { className: "sak-unavailable-state__title", children: title }), description ? _jsx("p", { className: "sak-unavailable-state__desc", children: description }) : null, action] }));
}
export function ReadOnlyBanner({ title = 'فقط مشاهده', children, className, ...rest }) {
    return (_jsxs("div", { className: cn('sak-readonly-banner', className), role: "status", ...rest, children: [_jsx("strong", { className: "sak-readonly-banner__title", children: title }), _jsx("div", { className: "sak-readonly-banner__body", children: children })] }));
}
//# sourceMappingURL=Feedback.js.map